import React, { useState } from 'react';
import { 
  Download, 
  Upload, 
  FileSpreadsheet, 
  Users, 
  Calendar, 
  Heart, 
  Activity as ActivityIcon, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Loader2, 
  FileText, 
  Info,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import AttendanceBulkUpload from './AttendanceBulkUpload';
import HealthBulkUpload from './HealthBulkUpload';
import ActivitiesBulkUpload from './ActivitiesBulkUpload';

const BulkUpload = () => {
  const [activeTab, setActiveTab] = useState('students');
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [resultTab, setResultTab] = useState('success'); // 'success' or 'errors'

  const tabs = [
    { id: 'students', label: 'Students', icon: Users },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'health', label: 'Health', icon: Heart },
    { id: 'activities', label: 'Activities', icon: ActivityIcon }
  ];

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await apiClient.get(`/v1/admin/bulk-upload/templates/${type}`, {
        responseType: 'blob'
      });
      
      const fileName = `${type}_bulk_upload_template.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/v1/admin/bulk-upload/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResults(response.data);
      
      const successful = response.data.successful || 0;
      const failed = response.data.failed || 0;
      const warningsCount = (response.data.warnings || []).length;
      
      if (successful > 0) {
        toast.success(`🎉 Successfully uploaded ${successful} records!`);
      }
      
      if (failed > 0 || warningsCount > 0) {
        toast.warning(`⚠️ ${failed} failed, ${warningsCount} warnings. Check results below.`);
        setResultTab('errors');
      } else {
        setResultTab('success');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="p-6 relative min-h-screen">
      {/* Loading Overlay */}
      {uploading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/60 backdrop-blur-md transition-all duration-300">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center max-w-sm w-full mx-4 border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative flex items-center justify-center mb-6">
              <div className="absolute w-16 h-16 rounded-full border-4 border-blue-100 animate-ping"></div>
              <div className="relative bg-blue-50 p-4 rounded-full text-blue-600">
                <Loader2 className="w-10 h-10 animate-spin" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Processing Data</h3>
            <p className="text-sm text-slate-500 text-center mb-4 leading-relaxed">
              We are parsing your Excel sheet, validating formats, creating student profiles and mapping parent accounts.
            </p>
            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full w-2/3 rounded-full animate-pulse"></div>
            </div>
            <span className="text-xs text-blue-600 font-medium mt-3">Please do not refresh or close this tab</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-8 h-8 text-blue-600" />
            Bulk Operations Portal
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">
            Import new student registers, update attendance, health status, and activities in bulk.
          </p>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-6 overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50">
          <div className="flex overflow-x-auto scrollbar-none">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setUploadResults(null);
                  }}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-white shadow-sm'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label} Import
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content Panel */}
        <div className="p-6 md:p-8">
          {activeTab === 'attendance' && <AttendanceBulkUpload />}
          {activeTab === 'health' && <HealthBulkUpload />}
          {activeTab === 'activities' && <ActivitiesBulkUpload />}
          
          {activeTab === 'students' && (
            <div className="space-y-8 animate-in fade-in-50 duration-200">
              {/* Step 1 & 2 Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Step 1: Download */}
                <div className="border border-slate-150 rounded-xl p-5 hover:shadow-md transition-shadow bg-gradient-to-br from-slate-50 to-white flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-sm shadow-sm">1</span>
                      <h3 className="text-lg font-bold text-slate-800">Download Official Template</h3>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                      Get the pre-formatted Excel template. It includes specific structures, validation rules, and class list options to ensure correct data import.
                    </p>
                  </div>
                  <button
                    onClick={() => handleDownloadTemplate(activeTab)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm shadow-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download Student Template
                  </button>
                </div>

                {/* Step 2: Upload */}
                <div className="border border-slate-150 rounded-xl p-5 hover:shadow-md transition-shadow bg-gradient-to-br from-slate-50 to-white flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold flex items-center justify-center text-sm shadow-sm">2</span>
                      <h3 className="text-lg font-bold text-slate-800">Upload Completed File</h3>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                      After entering student and parent details, upload your Excel spreadsheet (.xlsx / .xls) file here. The system will parse and validate each record.
                    </p>
                  </div>
                  
                  <label className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer font-medium text-sm shadow-sm">
                    <Upload className="w-4 h-4" />
                    <span>Upload Student Records</span>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => handleFileUpload(e, activeTab)}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Upload Results Dashboard */}
              {uploadResults && (
                <div className="mt-8 border border-slate-200 rounded-xl overflow-hidden shadow-md animate-in slide-in-from-bottom-4 duration-300 bg-white">
                  {/* Results Header */}
                  <div className="bg-slate-900 text-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-400" />
                        Execution Report
                      </h3>
                      <p className="text-slate-400 text-xs mt-0.5">Parsed sheet & mapped models in DB</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-slate-800 rounded text-slate-300 font-mono">
                        Total: {uploadResults.total || 0}
                      </span>
                    </div>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border-b">
                    <div className="bg-white p-5 text-center">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Total Processed</div>
                      <div className="text-3xl font-extrabold text-slate-800">{uploadResults.total || 0}</div>
                    </div>
                    <div className="bg-white p-5 text-center">
                      <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">Successfully Imported</div>
                      <div className="text-3xl font-extrabold text-green-600">{uploadResults.successful || 0}</div>
                    </div>
                    <div className="bg-white p-5 text-center">
                      <div className="text-xs font-semibold text-yellow-600 uppercase tracking-wider mb-1">Skipped / Duplicates</div>
                      <div className="text-3xl font-extrabold text-yellow-600">{uploadResults.skipped || 0}</div>
                    </div>
                    <div className="bg-white p-5 text-center">
                      <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Failed Rows</div>
                      <div className="text-3xl font-extrabold text-red-600">{uploadResults.failed || 0}</div>
                    </div>
                  </div>

                  {/* Detail Panel Tabs */}
                  <div className="flex border-b border-slate-100 bg-slate-50/50 p-2 gap-2">
                    <button
                      onClick={() => setResultTab('success')}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                        resultTab === 'success'
                          ? 'bg-white text-slate-850 shadow-sm border border-slate-150'
                          : 'text-slate-500 hover:text-slate-850 hover:bg-slate-100'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Success Log ({uploadResults.created_students?.length || 0})
                    </button>
                    <button
                      onClick={() => setResultTab('errors')}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                        resultTab === 'errors'
                          ? 'bg-white text-slate-850 shadow-sm border border-slate-150'
                          : 'text-slate-500 hover:text-slate-850 hover:bg-slate-100'
                      }`}
                    >
                      <XCircle className="w-4 h-4 text-red-500" />
                      Failed & Skipped Log ({(uploadResults.errors?.length || 0) + (uploadResults.warnings?.length || 0)})
                    </button>
                  </div>

                  {/* Results Lists */}
                  <div className="p-6">
                    {resultTab === 'success' && (
                      <div className="space-y-4">
                        {/* Dynamic Metadata info */}
                        <div className="flex flex-wrap gap-4 text-xs">
                          {uploadResults.created_classes && uploadResults.created_classes.length > 0 && (
                            <div className="bg-blue-50 border border-blue-100 text-blue-700 px-3 py-2 rounded-lg flex items-center gap-2">
                              <BookOpen className="w-3.5 h-3.5" />
                              <span>Classes added: <strong>{uploadResults.created_classes.join(', ')}</strong></span>
                            </div>
                          )}
                          {uploadResults.created_parents && uploadResults.created_parents.length > 0 && (
                            <div className="bg-purple-50 border border-purple-100 text-purple-700 px-3 py-2 rounded-lg flex items-center gap-2">
                              <Users className="w-3.5 h-3.5" />
                              <span>Parent credentials auto-generated: <strong>{uploadResults.created_parents.length}</strong></span>
                            </div>
                          )}
                        </div>

                        {uploadResults.created_students && uploadResults.created_students.length > 0 ? (
                          <div className="border border-slate-100 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50 border-b border-slate-100 text-slate-600">
                                <tr>
                                  <th className="px-4 py-3 text-left font-semibold">Student ID</th>
                                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                                  <th className="px-4 py-3 text-left font-semibold">Assigned Class</th>
                                  <th className="px-4 py-3 text-right font-semibold">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-105 text-slate-750">
                                {uploadResults.created_students.map((student, idx) => (
                                  <tr key={idx} className="hover:bg-slate-50/55 transition-colors">
                                    <td className="px-4 py-3 font-mono text-xs text-blue-700">{student.readable_id}</td>
                                    <td className="px-4 py-3 font-medium text-slate-800">{student.name}</td>
                                    <td className="px-4 py-3">{student.class}</td>
                                    <td className="px-4 py-3 text-right">
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                                        <CheckCircle className="w-3 h-3" /> Created
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-10 bg-slate-50/30 rounded-lg border border-dashed text-slate-400 text-sm">
                            No students were newly imported in this batch.
                          </div>
                        )}
                      </div>
                    )}

                    {resultTab === 'errors' && (
                      <div className="space-y-4">
                        {((uploadResults.errors && uploadResults.errors.length > 0) || 
                          (uploadResults.warnings && uploadResults.warnings.length > 0)) ? (
                          <div className="border border-slate-100 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50 border-b border-slate-100 text-slate-600">
                                <tr>
                                  <th className="px-4 py-3 text-left font-semibold w-24">Row</th>
                                  <th className="px-4 py-3 text-left font-semibold w-32">Status</th>
                                  <th className="px-4 py-3 text-left font-semibold">Reason / Detail</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 text-slate-700">
                                {/* Warnings/Skipped */}
                                {uploadResults.warnings && uploadResults.warnings.map((warn, idx) => {
                                  // Extract Row number if present
                                  const rowMatch = warn.match(/^Row\s+(\d+):/i);
                                  const rowNum = rowMatch ? rowMatch[1] : '-';
                                  const cleanWarn = rowMatch ? warn.substring(rowMatch[0].length).trim() : warn;
                                  return (
                                    <tr key={`warn-${idx}`} className="hover:bg-slate-50/50 bg-amber-50/10">
                                      <td className="px-4 py-3 font-semibold text-slate-500">Row {rowNum}</td>
                                      <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                          <AlertTriangle className="w-3 h-3" /> Skipped
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-amber-800 text-xs font-medium leading-relaxed">{cleanWarn}</td>
                                    </tr>
                                  );
                                })}

                                {/* Errors */}
                                {uploadResults.errors && uploadResults.errors.map((error, idx) => {
                                  const rowMatch = error.match(/^Row\s+(\d+):/i);
                                  const rowNum = rowMatch ? rowMatch[1] : '-';
                                  const cleanErr = rowMatch ? error.substring(rowMatch[0].length).trim() : error;
                                  return (
                                    <tr key={`err-${idx}`} className="hover:bg-slate-50/50 bg-red-50/10">
                                      <td className="px-4 py-3 font-semibold text-slate-500">Row {rowNum}</td>
                                      <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-red-50 text-red-700 border border-red-100">
                                          <XCircle className="w-3 h-3" /> Failed
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-red-800 text-xs font-medium leading-relaxed">{cleanErr}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-10 bg-slate-50/30 rounded-lg border border-dashed text-slate-400 text-sm">
                            <span className="text-green-600 font-semibold">Perfect Import!</span> No validation warnings or failures occurred.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Important Field Instructions Card */}
          <div className="mt-8 p-6 bg-amber-50/60 border-l-4 border-amber-500 rounded-xl">
            <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-amber-600" />
              Import Rules & Best Practices
            </h4>
            <ul className="text-sm text-slate-600 space-y-2 list-none">
              {activeTab === 'students' && (
                <>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Required columns:</strong> Student Name*, Age*, Gender*, Birth Date*, Class*, Father Name*, Primary Phone*, Parent Email*, Address*, Blood Group*.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Class selection:</strong> Select from the dropdown. Classes must already exist or they will be dynamically initialized.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Phone formats:</strong> Supports international format starting with '+' (e.g. +1234567890) and standard 10-15 digits. Duplicate combinations of Student Name + Phone will be skipped.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Parent synchronization:</strong> Accounts are mapped via phone number. If a parent is already registered, the student connects automatically. Otherwise, credentials are generated.</span>
                  </li>
                </>
              )}
              {activeTab !== 'students' && (
                <>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Student matching:</strong> Always use the unique Student ID (e.g. SUN-1001) generated by this portal.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Date patterns:</strong> Standard ISO formatting is required: <code>YYYY-MM-DD</code> (e.g. 2026-06-15).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Transactional processing:</strong> Individual rows that fail validation are reported but do not block other rows.</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;
